<script>
import _ from "lodash";
import navigation from "../components/global/navigation.vue";
import ArtworkIntakeUpsell from "../components/artwork-intake/ArtworkIntakeUpsell.vue";

const STATUS_LABEL = {
  new: "New",
  viewed: "Viewed",
  archived: "Archived",
  converted_to_proof: "Converted to Proof",
  converted_to_job: "Converted to Job",
};

export default {
  name: "ArtworkIntakeList",
  components: { navigation, ArtworkIntakeUpsell },
  data() {
    return {
      isLoading: true,
      submissions: [],
      search: "",
      embedOpen: false,
      embedSettings: null,
      embedLoading: false,
      copiedKey: null,
    };
  },
  computed: {
    isPremiumAccount() {
      const sku = _.get(this, "$A.Auth.account.subscriptionState.sku", "");
      if (sku.includes("starter")) return false;
      return sku.includes("standard") || sku.includes("premium") || sku.includes("enterprise");
    },
    filteredSubmissions() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.submissions;
      return this.submissions.filter(
        (s) =>
          (s.contactName || "").toLowerCase().includes(q) ||
          (s.contactEmail || "").toLowerCase().includes(q)
      );
    },
  },
  methods: {
    statusLabel(status) {
      return STATUS_LABEL[status] || status;
    },
    formatDate(iso) {
      return this.$A.FormatDateToString(iso);
    },
    openSettings() {
      this.$router.push("/artwork-intake/settings");
    },
    viewSubmission(submission) {
      this.$router.push(`/artwork-intake/submission/${submission.id}`);
    },
    async openEmbed() {
      this.embedOpen = true;
      if (!this.embedSettings) {
        this.embedLoading = true;
        try {
          this.embedSettings = await this.$A.ArtworkIntakeService.getFormSettings();
        } catch (e) {
          console.error("Failed to load embed details:", e);
        }
        this.embedLoading = false;
      }
    },
    closeEmbed() {
      this.embedOpen = false;
      this.copiedKey = null;
    },
    async copy(text, key) {
      try {
        await navigator.clipboard.writeText(text);
        this.copiedKey = key;
        setTimeout(() => {
          if (this.copiedKey === key) this.copiedKey = null;
        }, 2000);
      } catch (e) {
        console.error("Clipboard copy failed:", e);
      }
    },
  },
  async mounted() {
    if (!this.isPremiumAccount) {
      this.isLoading = false;
      return;
    }
    try {
      this.submissions = await this.$A.ArtworkIntakeService.getAllSubmissions();
    } catch (e) {
      console.error("Failed to load artwork intake submissions:", e);
    }
    this.isLoading = false;
  },
};
</script>

<template>
  <div class="nav-margin">
    <navigation />
    <ArtworkIntakeUpsell v-if="!isPremiumAccount" />
    <template v-else>
      <div
        class="action-bar bg-white border-bottom border-gray-300 d-flex gap-12 justify-content-between flex-wrap py-10 px-4 sticky-top"
      >
        <div class="search-wrap">
          <img src="/img/search.svg" width="16" alt="search-icon" class="search-icon" />
          <input
            type="text"
            class="search-input"
            placeholder="Search"
            v-model="search"
          />
        </div>

        <div class="d-flex align-items-center gap-12">
          <button class="tertiary-btn" @click="openEmbed">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            Embed
          </button>
          <button class="settings-btn" @click="openSettings">
            <img src="/img/cog.svg" width="14" alt="settings-icon" />
            Form Settings
          </button>
        </div>
      </div>

      <div class="content-view">
        <div v-if="isLoading" class="px-4 py-4 text-gray-500">Loading submissions...</div>

        <ul
          v-else-if="filteredSubmissions.length"
          class="submission-list list-unstyled mb-0"
        >
          <li
            v-for="submission in filteredSubmissions"
            :key="submission.id"
            class="submission-row border-bottom border-opacity-10 d-flex gap-2 justify-content-between align-items-center px-4 py-14 bg-white"
            @click="viewSubmission(submission)"
          >
            <div class="flex-fill d-flex align-items-center gap-3 min-w-0">
              <div class="avatar flex-shrink-0">
                {{ submission.contactName ? submission.contactName.charAt(0) : "?" }}
              </div>
              <div class="min-w-0">
                <div class="fs-14 fw-normal text-gray-900 mb-0 text-truncate">
                  {{ submission.contactName }}
                </div>
                <div class="fs-12 text-gray-500 text-truncate">
                  {{ submission.contactEmail }}
                </div>
              </div>
            </div>

            <div class="d-flex align-items-center gap-12 flex-shrink-0 row-meta">
              <span class="meta-chip" v-if="submission.fileCount">
                <img src="/img/icon-attachment.svg" width="12" alt="files" class="meta-icon" />
                {{ submission.fileCount }}
              </span>
              <span class="status-pill" :class="'status-' + submission.status">
                {{ statusLabel(submission.status) }}
              </span>
              <span class="fs-12 text-gray-500 submitted-cell">
                {{ formatDate(submission.submittedAt) }}
              </span>
            </div>
          </li>
        </ul>

        <div v-else class="empty-wrap">
          <div class="empty-card">
            <div class="empty-hero">
              <div class="empty-illustration" aria-hidden="true">
                <div class="illu-form">
                  <div class="illu-form-bar primary"></div>
                  <div class="illu-form-bar"></div>
                  <div class="illu-form-bar short"></div>
                  <div class="illu-form-btn"></div>
                </div>
                <svg class="illu-arrow" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
                <div class="illu-stack">
                  <div class="illu-card illu-card-3"></div>
                  <div class="illu-card illu-card-2"></div>
                  <div class="illu-card illu-card-1">
                    <div class="illu-paper-line"></div>
                    <div class="illu-paper-line short"></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="empty-headline">
              <h2>Start receiving artwork from your customers</h2>
              <p>Embed a form on your site or share a link. Files and contact details land here, ready to turn into proofs.</p>
            </div>

            <ol class="empty-steps">
              <li>
                <span class="step-num">1</span>
                <div class="step-body">
                  <div class="step-title">Configure your form</div>
                  <div class="step-help">Pick the proof custom fields you want customers to fill out.</div>
                </div>
              </li>
              <li>
                <span class="step-num">2</span>
                <div class="step-body">
                  <div class="step-title">Share it</div>
                  <div class="step-help">Copy the link to send in email, or paste the embed snippet onto your website.</div>
                </div>
              </li>
              <li>
                <span class="step-num">3</span>
                <div class="step-body">
                  <div class="step-title">Receive submissions</div>
                  <div class="step-help">Every submission becomes a contact and shows up right here.</div>
                </div>
              </li>
            </ol>

            <div class="empty-actions">
              <button class="primary-btn" @click="openSettings">Configure form</button>
              <button class="ghost-btn" @click="openEmbed">Get embed code</button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-if="embedOpen" class="embed-modal-backdrop" @click.self="closeEmbed">
      <div class="embed-modal" role="dialog" aria-labelledby="embed-modal-title">
        <div class="embed-modal-header">
          <div>
            <h3 id="embed-modal-title" class="embed-modal-title">Share your intake form</h3>
            <p class="embed-modal-sub">Copy the link or paste the embed snippet onto your website.</p>
          </div>
          <button class="embed-close" @click="closeEmbed" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div v-if="embedLoading" class="embed-loading">Loading...</div>
        <div v-else-if="embedSettings" class="embed-modal-body">
          <div class="embed-section">
            <label class="embed-label">Public link</label>
            <p class="embed-help">Send this in an email or link to it from your site.</p>
            <div class="embed-row">
              <input type="text" class="embed-field" :value="embedSettings.publicFormUrl" readonly />
              <button class="embed-copy" @click="copy(embedSettings.publicFormUrl, 'url')">
                {{ copiedKey === "url" ? "Copied" : "Copy" }}
              </button>
            </div>
          </div>

          <div class="embed-section">
            <label class="embed-label">Embed code</label>
            <p class="embed-help">Paste this where you want the form to appear on your website.</p>
            <div class="embed-row">
              <textarea class="embed-field embed-textarea" :value="embedSettings.embedSnippet" readonly rows="3" />
              <button class="embed-copy" @click="copy(embedSettings.embedSnippet, 'snippet')">
                {{ copiedKey === "snippet" ? "Copied" : "Copy" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.search-wrap {
  position: relative;
  width: 320px;
  max-width: 100%;
}
.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.55;
  pointer-events: none;
}
.search-input {
  width: 100%;
  height: 36px;
  padding: 0 12px 0 36px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
  background: #fff;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.search-input:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.search-input::placeholder {
  color: #9ca3af;
}

.settings-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.settings-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}
.settings-btn img {
  opacity: 0.7;
}

.tertiary-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 10px;
  background: transparent;
  border: 0;
  color: #4b5563;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 120ms ease, color 120ms ease;
}
.tertiary-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.embed-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 80px;
  z-index: 1080;
}
.embed-modal {
  background: #fff;
  width: 100%;
  max-width: 560px;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  overflow: hidden;
}
.embed-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 12px;
  border-bottom: 1px solid #f1f3f5;
}
.embed-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px;
}
.embed-modal-sub {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}
.embed-close {
  background: transparent;
  border: 0;
  color: #6b7280;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
}
.embed-close:hover {
  background: #f3f4f6;
  color: #111827;
}
.embed-loading {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}
.embed-modal-body {
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.embed-section {
  display: flex;
  flex-direction: column;
}
.embed-label {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
}
.embed-help {
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 8px;
}
.embed-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
}
.embed-field {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #f9fafb;
  color: #1f2937;
  outline: none;
  resize: none;
}
.embed-textarea {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}
.embed-copy {
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 6px;
  padding: 0 16px;
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
  align-self: stretch;
}
.embed-copy:hover {
  background: #017a48;
}

.content-view {
  height: calc(100vh - 130px);
  overflow-y: auto;
}

.submission-list {
  background: #fff;
}

.submission-row {
  cursor: pointer;
  position: relative;
  transition: background-color 120ms ease;
}
.submission-row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #019256;
  opacity: 0;
  transition: opacity 120ms ease;
}
.submission-row:hover {
  background: #f3f4f6;
}
.submission-row:hover::before {
  opacity: 1;
}
.submission-row:hover .avatar {
  transform: scale(1.04);
}
.avatar {
  transition: transform 120ms ease;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #019256;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
}

.row-meta {
  font-size: 13px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 12px;
  font-weight: 500;
}
.meta-icon {
  opacity: 0.7;
}

.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: #ebebed;
  color: #444;
}
.status-new {
  background: #d1f7e3;
  color: #019256;
}
.status-viewed {
  background: #e6eefc;
  color: #2563eb;
}
.status-archived {
  background: #ebebed;
  color: #6b7280;
}
.status-converted_to_proof,
.status-converted_to_job {
  background: #f3e8ff;
  color: #7c3aed;
}

.submitted-cell {
  min-width: 96px;
  text-align: right;
}

.empty-wrap {
  padding: 56px 24px;
  display: flex;
  justify-content: center;
}

.empty-card {
  width: 100%;
  max-width: 640px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 40px 40px 32px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 12px 32px rgba(15, 23, 42, 0.05);
  position: relative;
  overflow: hidden;
}
.empty-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 120px;
  background: linear-gradient(180deg, rgba(1, 146, 86, 0.06) 0%, rgba(1, 146, 86, 0) 100%);
  pointer-events: none;
}

.empty-hero {
  display: flex;
  justify-content: center;
  position: relative;
  margin-bottom: 28px;
}
.empty-illustration {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  z-index: 1;
}
.illu-form {
  width: 80px;
  height: 96px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}
.illu-form-bar {
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
}
.illu-form-bar.primary {
  background: #019256;
  width: 70%;
}
.illu-form-bar.short {
  width: 50%;
}
.illu-form-btn {
  margin-top: auto;
  height: 12px;
  border-radius: 4px;
  background: #019256;
  width: 55%;
  opacity: 0.85;
}
.illu-arrow {
  color: #019256;
  opacity: 0.6;
}
.illu-stack {
  position: relative;
  width: 80px;
  height: 96px;
}
.illu-card {
  position: absolute;
  width: 80px;
  height: 70px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
}
.illu-card-1 {
  left: 0;
  top: 16px;
  z-index: 3;
  padding: 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-color: rgba(1, 146, 86, 0.4);
  box-shadow: 0 6px 16px rgba(1, 146, 86, 0.18);
}
.illu-card-2 {
  left: 6px;
  top: 8px;
  z-index: 2;
  background: #ecfdf5;
}
.illu-card-3 {
  left: 12px;
  top: 0;
  z-index: 1;
  background: #f3f4f6;
}
.illu-paper-line {
  height: 5px;
  background: #019256;
  border-radius: 3px;
  width: 100%;
  opacity: 0.65;
}
.illu-paper-line.short {
  width: 55%;
  opacity: 0.45;
}

.empty-headline {
  text-align: center;
  margin-bottom: 28px;
  position: relative;
  z-index: 1;
}
.empty-headline h2 {
  font-family: 'Open Sans', system-ui, -apple-system, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
}
.empty-headline p {
  font-size: 14px;
  line-height: 1.55;
  color: #6b7280;
  margin: 0 auto;
  max-width: 440px;
}

.empty-steps {
  list-style: none;
  padding: 20px;
  margin: 0 0 28px;
  background: #f9fafb;
  border: 1px solid #f1f3f5;
  border-radius: 10px;
  position: relative;
  z-index: 1;
}
.empty-steps li {
  display: flex;
  gap: 14px;
  padding: 10px 0;
  align-items: flex-start;
}
.empty-steps li + li {
  border-top: 1px solid #f1f3f5;
}
.step-num {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #d1d5db;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.step-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 2px;
}
.step-help {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.45;
}

.empty-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  position: relative;
  z-index: 1;
}
.primary-btn {
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(1, 146, 86, 0.18);
  transition: background-color 120ms ease;
}
.primary-btn:hover {
  background: #017a48;
}
.ghost-btn {
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.ghost-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

@media (max-width: 640px) {
  .action-bar {
    padding: 12px 16px !important;
    gap: 8px !important;
  }
  .search-wrap {
    width: 100%;
    max-width: 100%;
  }
  .action-bar > div:last-child {
    width: 100%;
  }
  .tertiary-btn,
  .settings-btn {
    flex: 1;
    justify-content: center;
  }

  .empty-wrap {
    padding: 24px 16px;
  }
  .empty-card {
    padding: 28px 20px 24px;
    border-radius: 12px;
  }
  .empty-card::before {
    height: 80px;
  }
  .empty-illustration {
    gap: 12px;
  }
  .illu-form,
  .illu-stack {
    width: 64px;
    height: 80px;
  }
  .illu-stack {
    width: 64px;
  }
  .illu-card {
    width: 64px;
    height: 60px;
  }
  .empty-headline h2 {
    font-size: 18px;
  }
  .empty-headline p {
    font-size: 13px;
  }
  .empty-steps {
    padding: 14px;
    margin-bottom: 20px;
  }
  .empty-actions {
    flex-direction: column;
  }
  .empty-actions .primary-btn,
  .empty-actions .ghost-btn {
    width: 100%;
  }

  .embed-modal-backdrop {
    padding: 16px;
    padding-top: 24px;
  }
  .embed-modal {
    border-radius: 10px;
  }
  .embed-modal-header,
  .embed-modal-body {
    padding-left: 16px;
    padding-right: 16px;
  }
  .embed-row {
    flex-direction: column;
  }
  .embed-copy {
    align-self: stretch;
    padding: 10px 16px;
  }

  .submission-row {
    padding: 12px 16px !important;
  }
  .row-meta {
    gap: 8px !important;
  }
  .submitted-cell {
    display: none;
  }
}

@media (max-width: 400px) {
  .row-meta .meta-chip {
    display: none;
  }
}
</style>
